import React, { useState, useEffect } from 'react';
import Joyride, { STATUS } from 'react-joyride';
import { useSelector } from 'react-redux';
import { getTheme } from '@icarius-pages/login/selectors';
import { getLocalizedString } from "@icarius-localization/strings";
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';

const CustomTooltip = ({
  index,
  step,
  backProps,
  closeProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
  isDark
}) => {
  // Removed kbdStyle as it's no longer needed

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' || e.key === 'ArrowRight') {
        e.preventDefault();
        e.stopPropagation();
        if (primaryProps && primaryProps.onClick) {
          primaryProps.onClick(e);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        e.stopPropagation();
        if (index > 0 && backProps && backProps.onClick) {
          backProps.onClick(e);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        if (skipProps && skipProps.onClick) {
          skipProps.onClick(e);
        } else if (closeProps && closeProps.onClick) {
          closeProps.onClick(e);
        }
      }
    };

    // Intercept in capture phase so other components don't block the keys
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [primaryProps, backProps, closeProps, skipProps, index]);

  return (
    <Box
      {...tooltipProps}
      style={{
        backgroundColor: isDark ? '#252525' : '#ffffff',
        color: isDark ? '#ffffff' : '#333333',
        borderRadius: '12px',
        padding: '20px 24px',
        boxShadow: isDark ? '0 10px 40px rgba(0,0,0,0.5)' : '0 10px 40px rgba(0,0,0,0.15)',
        width: 360,
        maxWidth: '100%',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      <IconButton
        size="small"
        onClick={skipProps ? skipProps.onClick : closeProps.onClick}
        style={{ position: 'absolute', top: 12, right: 12, color: isDark ? '#aaaaaa' : '#666666' }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
      <Box mb={1} pr={3}>
        <Typography variant="h6" style={{ fontWeight: 700, fontSize: '18px', margin: 0 }}>
          {step.title}
        </Typography>
      </Box>
      <Typography variant="body2" style={{ fontSize: '14px', lineHeight: 1.5, marginBottom: '40px', opacity: 0.9 }}>
        {step.content}
      </Typography>
      <Box display="flex" alignItems="center" style={{ position: "absolute", bottom: 10, right: 10 }}>
        {
          index > 0 && (
            <Button
              {...backProps}
              style={{
                color: isDark ? '#aaaaaa' : '#666666',
                backgroundColor: isDark ? '#333333' : '#f0f0f0',
                fontWeight: 600,
                padding: '6px 12px',
                textTransform: 'none',
              }}
              startIcon={"←"}
            >
              {getLocalizedString('tourBack')}
            </Button>
          )
        }
        <Button
          {...primaryProps}
          variant="contained"
          color="primary"
          style={{
            fontWeight: 600,
            padding: '6px 16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            textTransform: 'none',
            marginLeft: '8px',
            border: 0
          }}
          endIcon={!isLastStep && "→"}
        >
          {isLastStep ? getLocalizedString('tourFinish') : getLocalizedString('tourNext')}
        </Button>
      </Box>
    </Box>
  );
};

const AppTour = ({ steps, tourKey, isOpen, forceRun, onFinish }) => {
  const themeState = useSelector(getTheme);
  const isDark = themeState?.theme === 'dark';

  const [runTour, setRunTour] = useState(false);

  useEffect(() => {
    if (forceRun) {
      setRunTour(true);
      return;
    }

    if (isOpen) {
      const hasSeenTour = localStorage.getItem(tourKey);
      if (!hasSeenTour) {
        setRunTour(true);
      }
    } else {
      setRunTour(false);
    }
  }, [isOpen, forceRun, tourKey]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      localStorage.setItem(tourKey, 'true');
      setRunTour(false);
      if (onFinish) {
        onFinish();
      }
    }
  };

  return (
    <Joyride
      callback={handleJoyrideCallback}
      continuous
      disableScrolling
      disableKeyboard
      hideCloseButton
      run={runTour}
      showSkipButton
      steps={steps}
      tooltipComponent={(props) => <CustomTooltip {...props} isDark={isDark} />}
      styles={{
        options: {
          zIndex: 10000,
          arrowColor: isDark ? '#252525' : '#ffffff',
        }
      }}
    />
  );
};

export default AppTour;
